// Tournament play (player side).
//
// Participation is assumed (no invite/accept) — a connected group member just
// sees their tournament on the course-select screen and taps in, dropping
// straight into a pre-seeded round (course + their group's players + assigned
// tees + the tournament's games). The first connected player to tap claims the
// group's scorer slot.

import { ActiveGame, ActiveRound, createSeededRound } from "./activeRound";
import { GAMES_CATALOG, defaultOptionsFor } from "./gamesCatalog";
import { Course, Player, ScoreMap } from "./types";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/caddiegames`;

export interface TournamentPlayerInfo {
  name: string;
  handicapIndex?: number;
  // Tee the admin assigned this player (tee id on the course).
  tee?: string;
}

// One group in the full draw (every group of the tournament) — used by the
// pre-date read-only view so a player can see the whole field + tee times.
export interface PlayerTournamentGroup {
  id: string;
  name: string;
  teeTime?: string;
  players: TournamentPlayerInfo[];
  // Whether this group's card has been submitted (scorer ended the round).
  final?: boolean;
}

export interface PlayerTournament {
  id: string;
  tournamentId: string;
  tournamentName: string;
  date: string;
  course: Course;
  groupId: string;
  groupName: string;
  // The caller's own group tee time.
  teeTime?: string;
  players: TournamentPlayerInfo[];
  games: ActiveGame[];
  // True once the caller's own group card is final — the client drops the
  // tournament from the Play list (their participation is done).
  groupFinal?: boolean;
  // The full draw (all groups) for the pre-date read-only view.
  groups?: PlayerTournamentGroup[];
}

// Open/live tournaments the signed-in player is a group member of. Fails soft
// to an empty list. The caller filters to the tournament's date for display.
export async function loadMyTournaments(): Promise<PlayerTournament[]> {
  try {
    const res = await fetch(`${BASE}/tournaments/mine`, {
      credentials: "include",
    });
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body.tournaments)
      ? (body.tournaments as PlayerTournament[])
      : [];
  } catch {
    return [];
  }
}

// One group's submitted players + scores, as returned by the tournament-wide
// scores fetch.
export interface GroupScores {
  groupId: string;
  groupName?: string;
  teeTime?: string;
  players: Player[];
  scores: ScoreMap;
  // Whether this group's card is final (scorer ended the round).
  final?: boolean;
}

// Submit the caller's group players + scores. The backend only accepts writes
// from the group's scorer (first writer claims the slot), so non-scorers are
// silently rejected. Best-effort. Pass final=true on End Round to mark the
// group's card submitted (sticky on the server).
export async function submitGroupScores(
  tournamentId: string,
  groupId: string,
  groupName: string,
  players: Player[],
  scores: ScoreMap,
  final = false,
): Promise<void> {
  try {
    await fetch(
      `${BASE}/tournaments/${encodeURIComponent(tournamentId)}/scores`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ groupId, groupName, players, scores, final }),
      },
    );
  } catch {
    // best effort — the next push / leaderboard open re-submits
  }
}

// Every group's submitted players + scores for the tournament — merged into the
// full field for the cross-group leaderboard. Fails soft to [].
export async function fetchTournamentScores(
  tournamentId: string,
): Promise<GroupScores[]> {
  try {
    const res = await fetch(
      `${BASE}/tournaments/${encodeURIComponent(tournamentId)}/scores`,
      { credentials: "include" },
    );
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body.groups) ? (body.groups as GroupScores[]) : [];
  } catch {
    return [];
  }
}

// Claim the group scorer slot (first-claim-wins on the server). Best-effort —
// failure doesn't block the player from opening the round.
export async function claimScorer(
  tournamentId: string,
  groupId: string,
): Promise<void> {
  try {
    await fetch(
      `${BASE}/tournaments/${encodeURIComponent(tournamentId)}/scorer`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ groupId }),
      },
    );
  } catch {
    // best effort
  }
}

// Today's calendar date as yyyy-mm-dd (local) — used to gate which tournaments
// can be STARTED (only on the day of the event); upcoming ones still surface.
export function todayLocalISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Friendly label for a yyyy-mm-dd tournament date, e.g. "Sat, Jun 28". Parsed
// from parts (local midnight) so it doesn't shift across the UTC boundary.
export function formatTournamentDate(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// Map a tournament into a ready-to-score round for the player's group. Each
// player keeps their assigned tee; anyone without one falls back to the middle
// tee (matching createActiveRound / the Players modal auto-tee logic).
//
// IMPORTANT: the round's games are the player's OWN side games, seeded with
// just the mandatory base (Stroke Play) — the same empty starting point as a
// normal round. The tournament's games (Skins/Stableford/etc.) are scored on
// the admin side and are deliberately NOT placed in the player's games list.
export function buildRoundFromTournament(t: PlayerTournament): ActiveRound {
  const tees = t.course.tees || [];
  const middleTeeId =
    tees.length > 0 ? tees[Math.floor(tees.length / 2)].id : undefined;

  const players: Player[] = t.players.map((p) => ({
    id: `p_${Math.random().toString(36).slice(2, 10)}`,
    name: p.name,
    handicap: p.handicapIndex,
    tee: p.tee || middleTeeId,
  }));

  const sideGames: ActiveGame[] = GAMES_CATALOG.filter((g) => g.mandatory).map(
    (g) => ({ id: g.id, enabled: true, options: defaultOptionsFor(g.id) }),
  );

  // Total field size across every group (falls back to this group's size for
  // older payloads that don't carry the full draw).
  const tournamentPlayerCount =
    t.groups && t.groups.length > 0
      ? t.groups.reduce((sum, g) => sum + g.players.length, 0)
      : t.players.length;

  return {
    ...createSeededRound(t.course, players, sideGames, t.tournamentId),
    // Keep the tournament's games for the Tournament leaderboard tab — separate
    // from the player's side games above.
    tournamentName: t.tournamentName,
    tournamentPlayerCount,
    tournamentGames: t.games,
    tournamentGroupId: t.groupId,
    tournamentGroupName: t.groupName,
  };
}
